/**
 * `recordNonDelivery` — the ONE way the Film-Print station records that
 * NOTHING reached the RIP for a garment (phase 50.1 post-impl review,
 * blocker A).
 *
 * The station has five such exits, and they were five hand-rolled copies of the
 * same triple:
 *   1. `prep.artworkMissing`            — proven-absent artwork
 *   2. the agent drop returned !success — bytes never landed in the hot folder
 *   3. no drop payload at all           — e.g. the Azure legacy backend
 *   4. no DTF hot folder configured     — batch 1879's "10 sent, 8 landed"
 *   5. the combined-film fail-closed gate (plan 04)
 *
 * Exit 5 was missing `markNeedsManual`, and that single omission was the phase
 * blocker: the seed effect + a refetch repainted the failed unit as "At RIP",
 * and the batch-level "Mark all done" then swept it to FILM_PRINTED with no
 * film in existence. Extracting the triple makes the omission structurally
 * impossible — you cannot record a non-delivery without all three parts:
 *
 *   audit  (`reportOutcome(false, …)`)  → a FILM_DROP_FAILED prod_event exists
 *                                         server-side (batch 1672: a failure
 *                                         that never touches the audit relay is
 *                                         invisible outside the browser)
 *   warn   (line + optional modal)      → the operator is told, by film
 *   needs-manual                        → the amber cue survives every refetch,
 *                                         so the card can never be mistaken for
 *                                         a good drop and can never be swept
 *
 * Pure + dependency-injected (no React, no tRPC) so the invariant is
 * unit-tested alongside `handle-prepare-outcome.ts`'s.
 */

export interface NonDeliverySpec {
  /** Reason string for the prod_events audit row. Never rendered to an operator. */
  auditReason: string;
  /** i18n key for the blocking-modal title (single-unit runs only). */
  warnTitleKey: string;
  /** Already-translated operator message — used for BOTH the bulk summary line
   *  and the single-run modal body, so the two can never drift. */
  warnMessage: string;
  /** Explicit filename for the audit row. Omit (leave `undefined`) to let the
   *  caller's own `reportOutcome` fallback pick the filename — passing `null`
   *  deliberately audits "no filename". */
  filename?: string | null;
  /** Agent destination path, when the drop actually reached the agent. */
  destPath?: string;
  /**
   * Blocking-modal severity for a SINGLE-unit run:
   *   'error'   — nothing reached the RIP and there is no film row the operator
   *               can rescue by hand yet (combined-film gate, missing artwork).
   *   'warning' — the film row exists server-side and the manual "Mark Film
   *               Done" path still works (drop failed, no payload, no hot
   *               folder). Default.
   * Ignored in bulk runs (one reconciling summary owns severity there).
   */
  severity?: 'error' | 'warning';
  /** Stable discriminator for the console line — floor debugging greps this. */
  logKey: string;
}

export interface NonDeliveryDeps {
  /** The station's drop-outcome audit relay. Called exactly once, `success: false`. */
  reportOutcome: (success: boolean, error?: string, destPath?: string, filename?: string | null) => void;
  /** Persists the amber "Not sent — print manually" cue for this garment. */
  markNeedsManual: (garmentUnitId: string) => void;
  /** Records the operator line (always) and raises the blocking modal (single
   *  runs, unless the caller is holding it back for a pending auto-retry). */
  warn: (titleKey: string, message: string, severity: 'error' | 'warning') => void;
  /** console.warn sink. Injected so tests assert on it without spying globals. */
  log?: (line: string) => void;
}

/**
 * Record that nothing reached the RIP for `garmentUnitId`.
 *
 * Call order is audit → warn → needs-manual. It is not behaviourally
 * significant (the audit is fire-and-forget and the other two touch different
 * state), but it is fixed so the five exits produce identically-ordered
 * summary lines when a unit trips more than one warning in a run.
 */
export function recordNonDelivery(
  garmentUnitId: string,
  spec: NonDeliverySpec,
  deps: NonDeliveryDeps,
): void {
  deps.reportOutcome(false, spec.auditReason, spec.destPath, spec.filename);
  deps.warn(spec.warnTitleKey, spec.warnMessage, spec.severity ?? 'warning');
  deps.markNeedsManual(garmentUnitId);
  deps.log?.(
    `[film-print] nothing reached the RIP (${spec.logKey}) for garment ${garmentUnitId}: ${spec.auditReason}`,
  );
}
